import { Component, computed, effect, inject, input } from '@angular/core';
import { FieldTree, FormField } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { InfoMessageComponent } from '../../../shared/components/info-message/info-message.component';
import { DialogService } from '../../../shared/dialogs/dialog.service';
import { InputDialogType } from '../../../shared/dialogs/input/input.component';
import { LoadCalculationService } from './load-calculation.service';
import { Load } from './load.model';
import { PowerFactorComponent } from '../common/power-factor/power-factor.component';
import { SystemVoltage } from '../system-voltage/system-voltage.model';

// NOTE: In the future (smart loads), there will be a separate sub-form for
// each load type. For now, we will just have a single form (general-load) for all loads.

@Component({
  selector: 'cable-edit-load',
  imports: [FormField, MatSelectModule, MatInputModule, MatButtonToggleModule,
    PowerFactorComponent,
    InfoMessageComponent, MatButtonModule,MatIconModule],
  providers: [LoadCalculationService],
  templateUrl: './load.component.html'
})
export class LoadComponent {

  readonly form = input.required<FieldTree<Load>>();
  readonly systemVoltage = input.required<FieldTree<SystemVoltage>>();

  readonly calc = inject(LoadCalculationService);
  private dialogService = inject(DialogService);

  loadType = computed(() => this.calc.loadTypes().find(x => x.key === this.form().key().value()));
  requireEfficiency = computed(() => this.loadType()?.requireEfficiency ?? false);

  powerDC = this.calc.powerDC;
  powerAC = this.calc.powerAC;
  kvaAC = this.calc.kvaAC;

  onClickW() {
    this.dialogService.input(InputDialogType.number, 'Real power in W', 'enter W', 1e12, 1).subscribe(
      (x) => { if (x !== undefined && x !== null) this.calc.userWatts.set(x as number); }
    );
  }

  onClickVA() {
    this.dialogService.input(InputDialogType.number, 'Apparent power in VA', 'enter VA', 1e12, 1).subscribe(
      (x) => { if (x !== undefined && x !== null) this.calc.userVA.set(x as number); }
    );
  }

  constructor() {

    this.calc.init(this.form, this.systemVoltage);

    /// Reset 'case' if the voltage type changes.
    effect(() => {

      if (this.systemVoltage().case().value() === 'dc' && this.form().case().value() !== 'dc')
            this.form().case().value.set('dc');

      if (
        (this.systemVoltage().case().value() === 'ac1ph'
          || this.systemVoltage().case().value() === 'ac3ph'
          || this.systemVoltage().case().value() === 'tpn')
          && this.form().case().value() !== 'ac')
        this.form().case().value.set('ac');

    });

    /// Reset efficiency to 1 if load type doesn't require efficiency
    effect(() => {
      if (!this.requireEfficiency() &&
        this.form().efficiency().value() !== 1 &&
        this.form().key().dirty())
        this.form().efficiency().value.set(1);
    });

    /// If selected load type, not in option list, set to default (first in list)
    effect(() => {
      let noKeyInOptions = !this.calc.loadTypes().find(x => x.key === this.form().key().value());
      let keyDirty = this.form().key().dirty();
      let defaultKey = this.calc.loadTypes()[0]?.key;
      let keyNotEqualDefault = defaultKey !== this.form().key().value();

      if (noKeyInOptions && defaultKey && keyNotEqualDefault && keyDirty)
        this.form().key().value.set(defaultKey);

    });

    /// Set Ib, if calculated from user watts
    effect(() => {
      const i = this.calc.ibFromW();
      if (i !== undefined) {
        this.form().ib().value.set(i.roundTo(3));
        this.calc.userWatts.set(undefined);
      }
    });

    /// Set Ib, if calculated from user VA
    effect(() => {
      const i = this.calc.ibFromVA();
      if (i !== undefined) {
        this.form().ib().value.set(i.roundTo(3));
        this.calc.userVA.set(undefined);
      }
    });

  }

}
